import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Phone, Navigation, Pencil, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { salesApi, apiError } from '../salesApi';
import { useT, fmtDate, localeOf } from '../salesI18n';
import { VisitTypeBadge } from '../components/StatusBadge';
import { VisitFormDialog } from '../components/VisitFormDialog';
import { EmptyState } from '../components/EmptyState';

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(12, 0, 0, 0); return x; };
const mapsUrl = (addr) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr || '')}`;

export default function AgendaPage() {
  const { t, lang } = useT();
  const [mode, setMode] = useState('week');
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; });
  const [visits, setVisits] = useState(null);
  const [leads, setLeads] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);

  const range = useMemo(() => {
    if (mode === 'day') return { from: iso(anchor), to: iso(anchor) };
    const s = startOfWeek(anchor);
    return { from: iso(s), to: iso(addDays(s, 6)) };
  }, [mode, anchor]);

  const load = useCallback(async () => {
    try { const { data } = await salesApi.get('/visits', { params: { date_from: range.from, date_to: range.to } }); setVisits(data); } catch (e) { toast.error(apiError(e)); setVisits([]); }
  }, [range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { salesApi.get('/leads', { params: { status: 'new,visit_scheduled,measured,proposal_sent' } }).then((r) => setLeads(r.data)).catch(() => {}); }, []);

  const days = useMemo(() => {
    if (mode === 'day') return [iso(anchor)];
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => iso(addDays(s, i)));
  }, [mode, anchor]);

  const todayIso = iso(new Date());
  const shift = (n) => setAnchor((a) => addDays(a, mode === 'day' ? n : n * 7));
  const openNew = (date) => { setEditVisit(null); setDefaultDate(date || null); setFormOpen(true); };
  const setStatus = async (v, status) => { try { await salesApi.put(`/visits/${v.id}`, { status }); load(); } catch (e) { toast.error(apiError(e)); } };

  const title = mode === 'day'
    ? fmtDate(range.from, lang, { weekday: 'long', day: '2-digit', month: 'long' })
    : `${fmtDate(range.from, lang, { day: '2-digit', month: 'short' })} – ${fmtDate(range.to, lang, { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <div className="space-y-5" data-testid="sales-agenda-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t.visits}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 capitalize" data-testid="agenda-title">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-white/80 p-1 bg-white/60 backdrop-blur">
            {['day', 'week'].map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} data-testid={`agenda-mode-${m}`} className={`h-9 px-4 rounded-full text-xs font-semibold transition-all ${mode === m ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white/70'}`}>{t[m]}</button>
            ))}
          </div>
          <Button variant="outline" className="h-10 w-10 p-0" onClick={() => shift(-1)} data-testid="agenda-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-10" onClick={() => setAnchor(() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; })} data-testid="agenda-today">{t.backToToday}</Button>
          <Button variant="outline" className="h-10 w-10 p-0" onClick={() => shift(1)} data-testid="agenda-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button className="h-10 font-semibold" onClick={() => openNew()} data-testid="agenda-new-visit-btn"><Plus className="h-4 w-4 mr-1.5" /> {t.newVisit}</Button>
        </div>
      </div>

      {visits === null ? <p className="text-sm text-muted-foreground">{t.loading}</p> : visits.length === 0 && mode === 'day' ? (
        <EmptyState icon={CalendarDays} title={t.noVisits} action={<Button className="h-11" onClick={() => openNew(range.from)} data-testid="agenda-empty-new-visit"><Plus className="h-4 w-4 mr-1.5" />{t.newVisit}</Button>} testId="agenda-empty" />
      ) : (
        <div className={`grid gap-3 ${mode === 'week' ? 'lg:grid-cols-7' : ''}`} data-testid="agenda-grid">
          {days.map((d) => {
            const dayVisits = visits.filter((v) => v.date === d);
            const isToday = d === todayIso;
            return (
              <div key={d} data-testid={`agenda-day-${d}`} className={`rounded-xl border ${isToday ? 'border-primary/50 bg-accent/40' : 'border-border bg-card'} p-3 min-h-[90px] flex flex-col`}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>{new Date(`${d}T12:00:00`).toLocaleDateString(localeOf(lang), { weekday: 'short', day: '2-digit' })}</p>
                  <button type="button" onClick={() => openNew(d)} className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground" aria-label={t.newVisit} data-testid={`agenda-add-${d}`}><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <div className="space-y-2 flex-1">
                  {dayVisits.map((v) => (
                    <div key={v.id} data-testid={`agenda-visit-${v.id}`} className={`rounded-lg border border-border bg-card p-2.5 text-xs shadow-sm sales-card-hover ${v.status === 'cancelled' ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-mono font-bold text-sm">{v.time}</span>
                        <VisitTypeBadge type={v.type} />
                      </div>
                      <Link to={`/leads/${v.lead_id}`} className={`block font-semibold mt-1 hover:text-primary truncate ${v.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{v.lead_name}</Link>
                      {v.lead_address && <p className="text-muted-foreground truncate">{v.lead_address}</p>}
                      <p className="text-[10px] text-muted-foreground mt-0.5">{v.assigned_to_name || v.assigned_to} · {v.duration_min} min</p>
                      <div className="flex gap-1 mt-2">
                        {v.lead_phone && <a href={`tel:${v.lead_phone}`} className="h-8 w-8 rounded-md border border-border flex items-center justify-center hover:bg-accent" aria-label={t.call}><Phone className="h-3.5 w-3.5" /></a>}
                        {v.lead_address && <a href={mapsUrl(v.lead_address)} target="_blank" rel="noreferrer" className="h-8 w-8 rounded-md border border-border flex items-center justify-center hover:bg-accent" aria-label={t.navigate}><Navigation className="h-3.5 w-3.5" /></a>}
                        <button type="button" onClick={() => { setEditVisit(v); setDefaultDate(null); setFormOpen(true); }} className="h-8 w-8 rounded-md border border-border flex items-center justify-center hover:bg-accent" aria-label={t.edit} data-testid={`agenda-edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        {v.status === 'scheduled' && (
                          <>
                            <button type="button" onClick={() => setStatus(v, 'done')} className="h-8 w-8 rounded-md border border-emerald-200 text-emerald-700 flex items-center justify-center hover:bg-emerald-50 ml-auto" aria-label={t.markDone} data-testid={`agenda-done-${v.id}`}><Check className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={() => setStatus(v, 'cancelled')} className="h-8 w-8 rounded-md border border-rose-200 text-rose-700 flex items-center justify-center hover:bg-rose-50" aria-label={t.markCancelled} data-testid={`agenda-cancel-${v.id}`}><X className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {dayVisits.length === 0 && <p className="text-[11px] text-muted-foreground/70 text-center py-3">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <VisitFormDialog open={formOpen} onOpenChange={setFormOpen} leads={leads} visit={editVisit} defaultDate={defaultDate} onSaved={load} />
    </div>
  );
}
